import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { AbstractPageDataProvider } from "@/lib/utility/data/page-data-provider.abstract";
import { PageFilterTypes } from "@/lib/utility/data/page-filter-types.enum";
import { IPageViewModel } from "@/lib/utility/data/page-view-model.interface";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { IContract, Contract } from "@/models/contract.entity";
import contractFleetService from "@/services/mrfiktiv/services/fleet/fleetContractService";
import {
  MrfiktivFleetContractControllerGetAllParamsGen,
  MrfiktivContractViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { FleetContractDataAccessLayer } from "../access-layers/fleet-contract.access-layer";
import { PaginationFilterListElement } from "../base-pagination.store";
import { FleetManagerModule } from "./manager-fleet.store";
import { ContractTypeEnum } from "@/lib/enum/contract-type.enum";

@Module({
  dynamic: true,
  namespaced: true,
  name: "contractFleetStore",
  store
})
export class ContractFleetStore extends PaginatedBaseStore<
  IContract,
  MrfiktivFleetContractControllerGetAllParamsGen & { fleetIdFilter?: string[] }
> {
  _data = FleetContractDataAccessLayer;
  _pageProvider = new (class extends AbstractPageDataProvider<
    IContract,
    MrfiktivFleetContractControllerGetAllParamsGen
  > {
    async getPage(query: MrfiktivFleetContractControllerGetAllParamsGen): Promise<IPageViewModel<IContract>> {
      const res = await contractFleetService.findAll(query, { ids: FleetManagerModule.selectedFleetIds ?? [] });

      const meta = res.meta;
      const data = (res.data?.map((d) => new Contract(d)) ?? []) as IContract[];

      return { meta, data };
    }
  })();

  _pager = new PageDataHandler(this._data, this._pageProvider);

  hiddenFilter: IPageFilterElement[] = [];

  filterOptions: PaginationFilterListElement[] = [
    {
      key: "timestamp.created",
      type: PageFilterTypes.DATE,
      displayName: "objects.timestamp.created"
    },
    {
      key: "title",
      type: PageFilterTypes.STRING,
      displayName: "objects.contract.title"
    },
    {
      key: "description",
      type: PageFilterTypes.STRING,
      displayName: "objects.contract.description"
    },
    {
      key: "type",
      type: PageFilterTypes.ENUM,
      config: {
        items: Object.values(ContractTypeEnum).map((e) => {
          return {
            text: `enums.ContractTypeEnum.${e}`,
            value: e
          };
        }),
        itemValue: "value"
      },
      displayName: "objects.contract.type"
    },
    {
      key: "startDate",
      type: PageFilterTypes.DATE,
      displayName: "objects.contract.startDate"
    },
    {
      key: "endDate",
      type: PageFilterTypes.DATE,
      displayName: "objects.contract.endDate"
    }
  ].map((f) => new PaginationFilterListElement(f));

  _current: MrfiktivContractViewModelGen | null = null;

  get current() {
    return this._current;
  }

  @Mutation
  _mutateCurrent(data: MrfiktivContractViewModelGen) {
    this._current = data;
  }

  @Action
  setCurrent(data: MrfiktivContractViewModelGen) {
    this.context.commit("_mutateCurrent", data);
  }

  @Action
  async getOne(data: { partnerId: string; fleetId: string; id: string }): Promise<MrfiktivContractViewModelGen> {
    const response = await contractFleetService.findOne(data.partnerId, data.fleetId, data.id);

    this.context.commit("_mutateCurrent", response.data);

    return response.data;
  }
}

export const ContractFleetModule = getModule(ContractFleetStore);
